import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { useAnalyticsUsers } from '../../hooks/useApi';
import { downloadCSV } from '../../utils/csvExport';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardHeader,
    DataTable,
    EmptyState,
    Pagination,
    ProgressBar,
    useSort,
} from '../../components/ui';

const UsersTab = ({ filters }) => {
    const [page, setPage] = useState(1);
    const { data: result, isLoading } = useAnalyticsUsers({ ...filters, page });
    const users = useMemo(() => result?.data || [], [result]);
    const { sort, onSort } = useSort({ key: 'total_assigned', dir: 'desc' });

    useEffect(() => {
        setPage(1);
    }, [filters]);

    const sortedUsers = useMemo(() => {
        if (!sort) return users;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...users].sort((a, b) => {
            let av = a[sort.key];
            let bv = b[sort.key];
            if (typeof av === 'string' && !Number.isNaN(Number(av))) av = parseFloat(av);
            if (typeof bv === 'string' && !Number.isNaN(Number(bv))) bv = parseFloat(bv);
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
    }, [users, sort]);

    const totalPages = result?.totalPages || 1;
    const total = result?.total || sortedUsers.length;

    const columns = [
        {
            key: 'first_name',
            header: 'User',
            sortable: true,
            render: (u) => (
                <span className="flex items-center gap-2.5">
                    <Avatar name={`${u.first_name} ${u.last_name}`} size="md" />
                    <span className="min-w-0">
                        <span className="block font-medium text-ink-900 truncate">
                            {u.first_name} {u.last_name}
                        </span>
                        <span className="block text-micro text-ink-500 truncate">{u.role}</span>
                    </span>
                </span>
            ),
        },
        {
            key: 'total_assigned',
            header: 'Assigned',
            sortable: true,
            align: 'right',
            render: (u) => <span className="tabular font-medium">{u.total_assigned}</span>,
        },
        {
            key: 'completed',
            header: 'Completed',
            sortable: true,
            align: 'right',
            render: (u) => <span className="tabular text-success-strong">{u.completed}</span>,
        },
        {
            key: 'in_progress',
            header: 'In progress',
            sortable: true,
            align: 'right',
            render: (u) => <span className="tabular text-info-strong">{u.in_progress}</span>,
        },
        {
            key: 'pending',
            header: 'Pending',
            sortable: true,
            align: 'right',
            render: (u) => <span className="tabular text-ink-600">{u.pending}</span>,
        },
        {
            key: 'overdue',
            header: 'Overdue',
            sortable: true,
            align: 'right',
            render: (u) =>
                u.overdue > 0 ? (
                    <Badge variant="danger">{u.overdue}</Badge>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            key: 'completion_rate',
            header: 'Completion',
            sortable: true,
            width: 150,
            render: (u) => <ProgressBar value={u.completion_rate} showValue />,
        },
        {
            key: 'avg_completion_hours',
            header: 'Avg time',
            sortable: true,
            align: 'right',
            render: (u) => (
                <span className="tabular text-ink-600">
                    {u.avg_completion_hours ? `${Math.round(u.avg_completion_hours)} h` : '—'}
                </span>
            ),
        },
    ];

    return (
        <Card>
            <CardHeader
                title="User performance"
                subtitle="Assignment volume and completion across the selected period"
                actions={
                    <Button
                        size="sm"
                        icon={Download}
                        className="no-print"
                        onClick={() => downloadCSV(sortedUsers, 'users-report.csv')}
                        disabled={!sortedUsers.length}
                    >
                        Export CSV
                    </Button>
                }
            />

            <DataTable
                columns={columns}
                rows={sortedUsers}
                loading={isLoading}
                sort={sort}
                onSort={onSort}
                empty={
                    <EmptyState
                        title="No users match this selection"
                        description="Adjust the filters or widen the date range."
                    />
                }
            />

            {sortedUsers.length > 0 && totalPages > 1 && (
                <Pagination
                    className="no-print"
                    page={page}
                    totalPages={totalPages}
                    total={total}
                    pageSize={Math.ceil(total / totalPages)}
                    onPageChange={setPage}
                />
            )}
        </Card>
    );
};

export default UsersTab;
